/**
 * The picklist detail's ONE refresh path (owner 2026-10-08: "make sure this mechanism communicates … so it won't
 * overfetch or have multiple fetches run at the same time"). Opening the page, every action, the 30-second tick,
 * coming back to the tab, "Refresh now" and the posting settle-poll all ask HERE, and this decides whether a read
 * actually runs. Pure — the store injects the read, the apply and the clock — so vitest pins every rule.
 *
 * The rules, each the fix for a screen that would otherwise be wrong:
 *
 * 1. **One read at a time per picklist.** Two overlapping reads can land out of order and put older figures back on
 *    screen over newer ones.
 * 2. **`refresh` always ends on a read that started AFTER it was asked.** An action asks after it saved, so a read
 *    already running may predate the save: it gets ONE follow-up read, shared by everyone who asked meanwhile.
 * 3. **`refreshIfStale` never adds a read.** It joins the running or queued one, and otherwise starts one only when the
 *    last completed read (by anyone, success or failure) is older than its window. So the tick counts from the last
 *    refresh, whatever caused it, and a failing server is retried once per window, not on every tick.
 * 4. **A read that started before a local write is thrown away and re-read.** If the store's picklist changed while a
 *    read was out (a mutation's own answer, an optimistic posting card), the read describes the world before that
 *    write; applying it would undo the write on screen.
 * 5. **An answer for a picklist that is no longer open is dropped.**
 */

export interface RefreshCoordinatorDeps<T> {
  now: () => number;
  /** The server read. Rejects on failure; the rejection's message is what callers get back. */
  read: (picklistId: number) => Promise<T>;
  /** Whether the page still has this picklist open. */
  isOpen: (picklistId: number) => boolean;
  /** The store's current value for this picklist, compared by REFERENCE to detect a local write during a read. */
  snapshot: (picklistId: number) => unknown;
  /** Puts a read on screen. Called only for the open picklist and only when no local write landed during the read. */
  apply: (picklistId: number, data: T) => void;
}

export interface RefreshCoordinator {
  /** A read that started after this call, run or joined. Resolves to null, or the read's error message. */
  refresh: (picklistId: number) => Promise<string | null>;
  /** Joins a running or queued read; otherwise starts one only if the last one completed `maxAgeMs` ago or more. */
  refreshIfStale: (picklistId: number, maxAgeMs: number) => { started: boolean; done: Promise<string | null> };
  /** Forget everything: the page closed. A read still out finishes and is dropped by rule 5. */
  reset: () => void;
}

/** How many times one read is re-run because local writes kept landing under it (rule 4) before it gives up. */
export const MAX_REREADS = 2;

interface Slot {
  id: number;
  inFlight: Promise<string | null> | null;
  queued: Promise<string | null> | null;
  /** When the last read for this picklist COMPLETED, whatever its outcome. */
  lastAt: number | null;
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Could not refresh the picklist';
}

export function createRefreshCoordinator<T>(deps: RefreshCoordinatorDeps<T>): RefreshCoordinator {
  let slot: Slot | null = null;

  const slotFor = (id: number): Slot => {
    if (!slot || slot.id !== id) slot = { id, inFlight: null, queued: null, lastAt: null };
    return slot;
  };

  async function readOnce(s: Slot, rereads: number): Promise<string | null> {
    const before = deps.snapshot(s.id);
    let data: T;
    try {
      data = await deps.read(s.id);
    } catch (error) {
      return messageOf(error);
    }
    if (!deps.isOpen(s.id)) return null;
    if (deps.snapshot(s.id) !== before) {
      // Rule 4 — a local write landed while this read was out. Re-read rather than apply a picture from before it.
      return rereads < MAX_REREADS ? readOnce(s, rereads + 1) : null;
    }
    deps.apply(s.id, data);
    return null;
  }

  function start(s: Slot): Promise<string | null> {
    const flight = readOnce(s, 0).finally(() => {
      s.lastAt = deps.now();
      if (s.inFlight === flight) s.inFlight = null;
    });
    s.inFlight = flight;
    return flight;
  }

  return {
    refresh(id) {
      const s = slotFor(id);
      // The queue FIRST: between a read finishing and its queued follow-up starting, nothing is in flight, and a
      // request landing in that gap must join the follow-up (which starts after it), not start a second read.
      if (s.queued) return s.queued;
      if (!s.inFlight) return start(s);
      {
        const running = s.inFlight;
        const queued = running.then(() => {
          s.queued = null;
          // The page may have moved on while we waited; a read for a closed picklist would only be dropped.
          return slot === s && deps.isOpen(s.id) ? start(s) : null;
        });
        s.queued = queued;
      }
      return s.queued;
    },

    refreshIfStale(id, maxAgeMs) {
      const s = slotFor(id);
      if (s.queued) return { started: false, done: s.queued };
      if (s.inFlight) return { started: false, done: s.inFlight };
      if (s.lastAt !== null && deps.now() - s.lastAt < maxAgeMs) return { started: false, done: Promise.resolve(null) };
      return { started: true, done: start(s) };
    },

    reset() {
      slot = null;
    },
  };
}
